const DB_NAME = 'VisitasTecnicasDB';
const DB_VERSION = 3;
const STORE_CLIENTES = 'clientes_asignados';
const STORE_VISITAS = 'visitas_pendientes';
const STORE_ACTIVIDADES = 'tipos_actividad';

export const TIPOS_ACTIVIDAD_BASE = [
  { id: 'base-sello', nombre: 'Sello', activo: true },
  { id: 'base-reposicion', nombre: 'Reposicion', activo: true },
  { id: 'base-retiro-medidor', nombre: 'Retiro de Medidor', activo: true },
  { id: 'base-retiro-medidor-no-uso', nombre: 'Retiro de medidor por no uso o sellado mas de 1 ano', activo: true },
  { id: 'base-retiro-medidor-cliente', nombre: 'Retiro de medidor a solicitud de Cliente', activo: true },
  { id: 'base-levantamiento', nombre: 'Levantamiento', activo: true },
  { id: 'base-visita-corte-sin-carta', nombre: 'Visita Corte no efectuado, Sin Carta', activo: true },
  { id: 'base-cliente-autorepuesto', nombre: 'Notificar cliente autorepuesto', activo: true },
  { id: 'base-cheque-protestado', nombre: 'Notificacion cheque protestado', activo: true },
  { id: 'base-otras-notificaciones', nombre: 'Otras notificaciones especiales', activo: true },
  { id: 'base-otros', nombre: 'Otros, No relacionados con cliente', activo: true },
  { id: 'base-pagos-terreno', nombre: 'Pagos en terreno', activo: true },
  { id: 'base-retiro-regulador', nombre: 'Retiro de regulador de presion', activo: true },
];

export const initDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = (event) => {
      console.error('IndexedDB error:', event.target.error);
      reject(event.target.error);
    };

    request.onsuccess = (event) => {
      resolve(event.target.result);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // La store antigua usaba solo cod_cliente como clave y perdia registros
      // cuando existian varias gestiones para el mismo cliente.
      if (db.objectStoreNames.contains(STORE_CLIENTES)) {
        const clientesStore = event.target.transaction.objectStore(STORE_CLIENTES);
        if (clientesStore.keyPath !== 'storage_key') {
          db.deleteObjectStore(STORE_CLIENTES);
          db.createObjectStore(STORE_CLIENTES, { keyPath: 'storage_key' });
        }
      } else {
        db.createObjectStore(STORE_CLIENTES, { keyPath: 'storage_key' });
      }

      if (!db.objectStoreNames.contains(STORE_VISITAS)) {
        db.createObjectStore(STORE_VISITAS, { keyPath: 'id', autoIncrement: true });
      }

      if (!db.objectStoreNames.contains(STORE_ACTIVIDADES)) {
        const actividadesStore = db.createObjectStore(STORE_ACTIVIDADES, { keyPath: 'id' });
        TIPOS_ACTIVIDAD_BASE.forEach((actividad) => actividadesStore.put(actividad));
      }
    };
  });
};

const buildClienteStorageKey = (cliente) =>
  `${cliente.cod_cliente || 'sin-codigo'}::${cliente.num_gestion || 'sin-gestion'}`;

const crearOperationId = () => {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const random = Math.floor(Math.random() * 16);
    const value = char === 'x' ? random : ((random & 0x3) | 0x8);
    return value.toString(16);
  });
};

export const guardarTiposActividad = async (actividadesArray) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_ACTIVIDADES], 'readwrite');
    const store = transaction.objectStore(STORE_ACTIVIDADES);

    store.clear();

    actividadesArray.forEach((actividad, index) => {
      if (!actividad?.nombre) return;
      store.put({
        ...actividad,
        id: actividad.id ?? `actividad-${index}-${actividad.nombre}`,
      });
    });

    transaction.oncomplete = () => resolve(true);
    transaction.onerror = (e) => reject(e.target.error);
  });
};

export const obtenerTiposActividad = async () => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_ACTIVIDADES], 'readonly');
    const store = transaction.objectStore(STORE_ACTIVIDADES);
    const request = store.getAll();

    request.onsuccess = () => {
      const actividades = request.result.length ? request.result : TIPOS_ACTIVIDAD_BASE;
      resolve(actividades.filter((actividad) => actividad.activo !== false));
    };
    request.onerror = (e) => reject(e.target.error);
  });
};

export const guardarVisitaOffline = async (visitaPayload) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_VISITAS], 'readwrite');
    const store = transaction.objectStore(STORE_VISITAS);

    visitaPayload.operation_id = visitaPayload.operation_id || crearOperationId();
    visitaPayload.timestamp_guardado = visitaPayload.timestamp_guardado || new Date().getTime();

    const request = store.add(visitaPayload);

    request.onsuccess = () => resolve(true);
    request.onerror = (e) => reject(e.target.error);
  });
};

export const obtenerVisitasPendientes = async () => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_VISITAS], 'readonly');
    const store = transaction.objectStore(STORE_VISITAS);
    const request = store.getAll();

    request.onsuccess = () => resolve(request.result);
    request.onerror = (e) => reject(e.target.error);
  });
};

export const eliminarVisitaOffline = async (id) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_VISITAS], 'readwrite');
    const store = transaction.objectStore(STORE_VISITAS);
    const request = store.delete(id);

    request.onsuccess = () => resolve(true);
    request.onerror = (e) => reject(e.target.error);
  });
};

export const marcarClienteFinalizadoOffline = async (cliente) => {
  return marcarClienteEstadoOffline(cliente, 'finalizado', { finalizado_offline: true });
};

export const marcarClienteEstadoOffline = async (cliente, estado, extras = {}) => {
  if (!cliente?.cod_cliente || !cliente?.num_gestion) return false;

  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_CLIENTES], 'readwrite');
    const store = transaction.objectStore(STORE_CLIENTES);
    const storageKey = buildClienteStorageKey(cliente);
    const getRequest = store.get(storageKey);

    getRequest.onsuccess = () => {
      const storedCliente = getRequest.result;
      if (!storedCliente) {
        resolve(false);
        return;
      }

      store.put({
        ...storedCliente,
        estado,
        ...extras,
      });
    };
    getRequest.onerror = (e) => reject(e.target.error);
    transaction.oncomplete = () => resolve(true);
    transaction.onerror = (e) => reject(e.target.error);
  });
};

export const guardarClientesAsignados = async (clientesArray) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_CLIENTES], 'readwrite');
    const store = transaction.objectStore(STORE_CLIENTES);

    store.clear();

    clientesArray.forEach((cliente) => {
      store.put({
        ...cliente,
        storage_key: buildClienteStorageKey(cliente),
      });
    });

    transaction.oncomplete = () => resolve(true);
    transaction.onerror = (e) => reject(e.target.error);
  });
};

export const obtenerClientesAsignados = async () => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_CLIENTES], 'readonly');
    const store = transaction.objectStore(STORE_CLIENTES);
    const request = store.getAll();

    request.onsuccess = () =>
      resolve(request.result.map(({ storage_key, ...cliente }) => cliente));
    request.onerror = (e) => reject(e.target.error);
  });
};
